import { useEffect, useRef, useState } from 'react';
import config from '../config';
import { readSseStream } from '../helpers/sse';

const RECONNECT_MS = 3000;

// A LIFO buffer, not a log: ops wants to see the stream moving, and the last
// couple of minutes is all anyone reads. Older entries fall off the end.
const FEED_LIMIT = 30;

// Rounded: a bearing carries ~15 decimal places off the wire and a feed line
// is for reading, not for forensics.
const coord = (location) =>
  location ? `${location.lat.toFixed(5)}, ${location.lon.toFixed(5)} @ ${Math.round(location.bearing ?? 0)}°` : 'none';

const eta = (value) => (value ? `${value.minutesAway}m` : 'none');

/**
 * One line per change, so the feed says what moved rather than dumping the
 * state again. `updatedAt` is deliberately ignored — it changes on every
 * message and would drown everything else.
 */
export const describeChange = (previous, next) => {
  if (!next) return ['no live state'];
  if (!previous) return ['snapshot'];

  const changes = [];
  ['phase', 'rideStatus', 'tripStatus'].forEach((key) => {
    if (previous[key] !== next[key]) changes.push(`${key} ${previous[key]} → ${next[key]}`);
  });
  ['showDriverLocation', 'showPickupEta', 'showDropoffEta', 'showRoute'].forEach((key) => {
    if (previous[key] !== next[key]) changes.push(`${key} ${next[key] ? 'on' : 'off'}`);
  });
  if (eta(previous.pickupEta) !== eta(next.pickupEta)) changes.push(`pickupEta ${eta(next.pickupEta)}`);
  if (eta(previous.dropoffEta) !== eta(next.dropoffEta)) changes.push(`dropoffEta ${eta(next.dropoffEta)}`);
  if (Boolean(previous.routePolyline) !== Boolean(next.routePolyline)) {
    changes.push(`route ${next.routePolyline ? 'received' : 'cleared'}`);
  }
  if (coord(previous.driverLocation) !== coord(next.driverLocation)) {
    changes.push(`driver ${coord(next.driverLocation)}`);
  }

  // The server re-sends state on any ride change, including ones that leave
  // every field this panel renders untouched — a stationary car, a field the
  // panel does not show, a re-route with the same shape. Rather than saying
  // "nothing happened", which is not knowable from here, print what arrived.
  return changes.length ? changes : [JSON.stringify(next)];
};

/**
 * Streams one ride's live state off the admin endpoint, which reads the same
 * state the rider's own app is being shown.
 *
 * The server sends the whole state on every change (`snapshot`, then `state`),
 * so `state` needs no reduction — the last message is the current truth.
 * `feed` is the recent history of those messages, newest first, for watching
 * the stream move.
 *
 * `rider` is ops-only and separate from the rider-facing state: where the
 * rider last reported being while the ride waits for pick-up, else null.
 */
const useRideLive = (rideId) => {
  const [state, setState] = useState(null);
  const [rider, setRider] = useState(null);
  const [feed, setFeed] = useState([]);
  const [connected, setConnected] = useState(false);
  // Diffing needs the previous message, and reading it out of a state updater
  // would make the diff a side effect of rendering.
  const previous = useRef(null);

  useEffect(() => {
    setState(null);
    setRider(null);
    setFeed([]);
    previous.current = null;
    if (!rideId) return undefined;

    const controller = new AbortController();
    let retry = null;
    let stopped = false;

    const connect = async () => {
      try {
        await readSseStream({
          url: `${config.api.baseUrl}/admin/live/ride/${encodeURIComponent(rideId)}`,
          signal: controller.signal,
          onOpen: () => setConnected(true),
          onEvent: ({ event, data }) => {
            if (event === 'rider') {
              setRider(data);
              const line = data ? `rider ${data.lat.toFixed(5)}, ${data.lon.toFixed(5)}` : 'rider cleared';
              setFeed((current) =>
                [{ at: new Date().toISOString(), event, changes: [line] }, ...current].slice(0, FEED_LIMIT),
              );
              return;
            }
            if (event !== 'snapshot' && event !== 'state') return;
            const changes = describeChange(previous.current, data);
            previous.current = data;
            setState(data);
            setFeed((current) => [{ at: new Date().toISOString(), event, changes }, ...current].slice(0, FEED_LIMIT));
          },
        });
      } catch (e) {
        if (controller.signal.aborted) return;
      } finally {
        // Skipped after unmount: the component is gone, and setting state on it
        // is a React warning with no upside.
        if (!stopped) setConnected(false);
      }
      if (!stopped) retry = setTimeout(connect, RECONNECT_MS);
    };

    connect();

    return () => {
      stopped = true;
      clearTimeout(retry);
      controller.abort();
    };
  }, [rideId]);

  return { state, rider, feed, connected };
};

export default useRideLive;
